import { Button, Accordion, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconCopy, IconDeviceFloppy, IconHistory } from "@tabler/icons-react";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Form, type SubmitHandler, useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import RotationInfoModal from "src/components/pages/Settings/CrewSetUp/RotationInfoModal/RotationInfoModal";
import AssignedLogbooks from "src/components/pages/Settings/CrewSetUp/UserForm/AssignedLogbooks";
import CrewAuthButton from "src/components/pages/Settings/CrewSetUp/UserForm/CrewAuthButton";
import Details from "src/components/pages/Settings/CrewSetUp/UserForm/Details";
import AdditionalInfo from "src/components/pages/Settings/CrewSetUp/UserForm/AdditionalInfo";
import ProtectedInfo from "src/components/pages/Settings/CrewSetUp/UserForm/ProtectedInfo";
import SendMailButton from "src/components/pages/Settings/CrewSetUp/UserForm/SendMailButton";
import SetCredentialsButton from "src/components/pages/Settings/CrewSetUp/UserForm/SetCredentialsButton";
import CheckboxGroup from "src/components/shared/_FormInputs/CheckboxGroup/CheckboxGroup";
import Breadcrumbs from "src/components/shared/Breadcrumbs/Breadcrumbs";
import Loader from "src/components/shared/Loader/Loader";
import { useCheckPerm, useGetApi, usePostApi } from "src/hooks";
import { useSelector } from "src/redux/hooks";
import { ButtonsWrapper, Flex, PageContainer } from "src/styled";
import { getYachtId } from "src/utils";
import { Notification } from "src/utils/Notification";

import { LayoutContainer } from "../../../../styled/UserForm.styled";
import { type IUserForm } from "./UserForm.types";
import {
  buildCopiedUserValues,
  mapUserForm,
  mapUserSelects,
  withJoinDateDefault,
} from "./UserForm.utils";
import styles from "./UserForm.module.scss";

interface Props {
  isManagement?: boolean;
  isCreateUser?: boolean;
}

const UserForm = ({ isManagement = false, isCreateUser = false }: Props) => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const checkPerm = useCheckPerm();
  const { post, isLoading } = usePostApi();

  const clientid = useSelector(state => state.permsSlice.clientid);

  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(false);
  const [accordionValue, setAccordionValue] = useState<string | null>(
    "details",
  );
  const {
    data,
    setData,
    isLoading: isDataLoading,
    error: dataError,
  } = useGetApi({
    request: isManagement ? "user-form-man" : "user-form",
    body: {
      params: { userid: userId ?? "new", yachtid: getYachtId() },
    },
    mapper: mapUserForm,
  });
  // isLoading starts false until the fetch effect runs, so also wait on !data
  const showLoader = isDataLoading || (!data && !dataError);
  const [searchParams] = useSearchParams();
  const copyFrom = searchParams.get("copyFrom");

  const { data: copyFromData } = useGetApi({
    request: isManagement ? "user-form-man" : "user-form",
    body: {
      params: { userid: copyFrom ?? "new", yachtid: getYachtId() },
    },
    mapper: mapUserForm,
    fetchOnInit: !!copyFrom,
  });

  const today = useMemo(() => dayjs().format("YYYY-MM-DD"), []);

  const initialValues = useMemo(
    () =>
      data?.initialValues
        ? withJoinDateDefault(data.initialValues, !userId, today)
        : undefined,
    [data, userId, today],
  );

  const copiedValues = useMemo(() => {
    if (!copyFrom || !initialValues || !copyFromData?.initialValues) {
      return undefined;
    }
    return withJoinDateDefault(
      buildCopiedUserValues(initialValues, copyFromData.initialValues),
      !userId,
      today,
    );
  }, [copyFrom, initialValues, copyFromData, userId, today]);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    reset,
    trigger,
    formState,
    getValues,
  } = useForm<IUserForm>({
    mode: "onChange",
    values: copyFrom ? copiedValues : initialValues,
  });

  useEffect(() => {
    if (copiedValues) {
      trigger(["fname", "sname", "email"]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copiedValues]);

  const updatePerms = useCallback(
    (data: any) => {
      const result = mapUserSelects(data);
      const perms = [
        ...result.userPerms.map(perm => perm.value),
        ...result.adminPerms.map(perm => perm.value),
      ];
      setValue("perms", perms);
      return result;
    },
    [setValue],
  );

  const { data: selectsData, refetch: refetchSelects } = useGetApi({
    request: "user-form-man-type",
    mapper: updatePerms,
    fetchOnInit: false,
  });
  const handleTypeChange = async (type: string) => {
    await refetchSelects({ params: { type } });
  };
  // const handleRankChange = async (role: string) => {
  //   console.log(role);
  //   if (role === MEDRANK && data) {
  //     if (data.selects.logbooks.find(lb => lb.value === MEDLBID)) return;
  //     const newLbs = deepClone(data.selects.logbooks);
  //     newLbs.push({ label: "Medical Log", value: MEDLBID });
  //     console.log(newLbs);
  //     setData(
  //       deepClone({
  //         ...data,
  //         selects: { ...data.selects, logbooks: newLbs },
  //       }),
  //     );
  //   }
  // };
  const submit: SubmitHandler<IUserForm> = async data => {
    const reqParams: Record<string, string> = {};
    const params = Object.fromEntries(searchParams.entries());

    ["orgid", "yachtid", "clientid"].forEach(param => {
      if (params[param] && params[param] !== "NULL") {
        reqParams[param] = params[param];
      } else if (param === "yachtid") {
        reqParams.yachtid = getYachtId();
      } else if (param === "clientid" && clientid) {
        reqParams.clientid = clientid;
      }
    });

    await post({
      request: "save-user",
      body: {
        params: {
          userid: userId ?? "new",
          ...reqParams,
          ...data,
        },
      },
      onSuccess: async res => {
        if (res?.requiresOverride) {
          // OPEN ROTATION INFO MODAL WHEN REQUIRES OVERRIDE
          open();
        } else {
          reset(data);
          Notification(
            "Success",
            `User ${userId ? "updated" : "created"} successfully.`,
            "green",
          );

          if (!userId) {
            if (isCreateUser && sendWelcomeEmail && res?.newUserId) {
              await post({
                request: "send-welcome",
                body: { params: { userid: res?.newUserId } },
              });
            }

            if (isManagement) navigate("/management/users");
            else navigate("/settings/userlist");
          }
        }
      },
    });
  };

  const validateForm = async () => {
    const result = await trigger();
    if (!result) {
      Notification("Validation Error", "Fill in the required fields", "red");
    }
    return result;
  };

  const onSubmit = async (authpw: string) => {
    const result = await trigger();
    if (result) {
      setValue("authpw", authpw);
      await handleSubmit(submit)();
    } else {
      Notification("Validation Error", "Fill in the required fields", "red");
    }
  };

  const handleCopyToNewUser = () => {
    if (!userId) return;

    const params = new URLSearchParams();
    ["orgid", "yachtid", "clientid"].forEach(param => {
      const value = searchParams.get(param);
      if (value && value !== "NULL") {
        params.set(param, value);
      }
    });
    params.set("copyFrom", userId);

    const basePath = isManagement
      ? "/management/users/create-user"
      : "/settings/userlist/create-user";

    navigate(`${basePath}?${params.toString()}`);
  };

  const disabled = !!(userId && !checkPerm("editeuser"));
  // const selects = useMemo(() => {
  //     const adminPerms =
  //       (selectsData ? selectsData.adminPerms : data?.selects.adminPerms) ?? [];
  //     return {
  //       ranks: (selectsData ? selectsData?.roles : data?.selects.roles) ?? [],
  //       userPerms:
  //         (selectsData ? selectsData.userPerms : data?.selects.userPerms)?.map(
  //           el => {
  //             if (adminPerms.length > 0) return el;
  //
  //             const copy = deepClone(el);
  //             if (copy.value.includes("edit") || copy.value.includes("delete")) {
  //               copy.value = "";
  //             }
  //             return copy;
  //           },
  //         ) ?? [],
  //       adminPerms,
  //     };
  //   }, [selectsData, data]);

  // const parsedSelectsData = useMemo(
  //   () => ({
  //     ...selectsData,
  //     userPerms:
  //       selectsData?.userPerms?.map(el => {
  //         if (selectsData.adminPerms.length > 0) return el;
  //         const copy = deepClone(el);
  //         if (copy.value.includes("edit") || copy.value.includes("delete")) {
  //           copy.value = "";
  //         }
  //         return copy;
  //       }) ?? [],
  //     adminPerms: selectsData?.adminPerms ?? [],
  //   }),
  //   [selectsData],
  // );

  // When copying, ranks/perms must match the copied user's type.
  const baseSelects = (copyFrom ? copyFromData : data)?.selects;
  const selects = useMemo(
    () => ({
      ranks: (selectsData ? selectsData?.roles : baseSelects?.roles) ?? [],
      userPerms:
        (selectsData ? selectsData.userPerms : baseSelects?.userPerms) ?? [],
      adminPerms:
        (selectsData ? selectsData.adminPerms : baseSelects?.adminPerms) ?? [],
    }),
    [selectsData, baseSelects],
  );
  const [opened, { open, close }] = useDisclosure(false);
  const userType = getValues("type") as unknown as number;

  const fname = useWatch({ control, name: "fname" });
  const sname = useWatch({ control, name: "sname" });

  const crewDisplayName = useMemo(
    () => `${fname ?? ""} ${sname ?? ""}`.trim(),
    [fname, sname],
  );

  const accOpenStyles = (v: string): CSSModuleClasses[string] => {
    return v === accordionValue ? styles.menuItemOpen : "";
  };

  return (
    <>
      <RotationInfoModal
        opened={opened}
        close={close}
        refetch={refetchSelects}
      />
      <PageContainer>
        <Form control={control} className={styles.form}>
          <Breadcrumbs w="100%">
            <ButtonsWrapper>
              {!showLoader && userId && (
                <>
                  <SendMailButton />
                  <Button
                    leftSection={<IconHistory size={"1.4rem"} />}
                    variant="outline"
                    onClick={() =>
                      navigate(`/settings/userlist/edit-user/${userId}/history`)
                    }>
                    History
                  </Button>
                  <SetCredentialsButton userId={userId} />
                  {checkPerm("createuser") && (
                    <Button
                      leftSection={<IconCopy size={"1.4rem"} />}
                      variant="outline"
                      onClick={handleCopyToNewUser}>
                      Copy to new user
                    </Button>
                  )}
                </>
              )}
              {!showLoader && !disabled && (
                <CrewAuthButton
                  onSubmit={onSubmit}
                  validate={validateForm}
                  leftSection={<IconDeviceFloppy size={"1.4rem"} />}
                  variant="outline"
                  loading={isLoading}>
                  Save
                </CrewAuthButton>
              )}
            </ButtonsWrapper>
          </Breadcrumbs>
          <Flex w="100%" h="calc(100% - 52px)" justify="flex-start">
            <LayoutContainer className={styles.content}>
              <Accordion
                defaultValue="details"
                w="100%"
                value={accordionValue}
                onChange={v => v && setAccordionValue(v)}
                chevron={null}
                classNames={{
                  item: styles.menuItem,
                  label: styles.menuLabel,
                  control: styles.menuControl,
                  panel: styles.menuPanel,
                  root: styles.menu,
                }}>
                <Flex className={styles.crewName}>
                  {showLoader ? "" : crewDisplayName || "New User"}
                </Flex>
                <Accordion.Item
                  value="details"
                  className={accOpenStyles("details")}>
                  <Accordion.Control>Details</Accordion.Control>
                </Accordion.Item>

                <Accordion.Item
                  value="logbooks"
                  className={accOpenStyles("logbooks")}>
                  <Accordion.Control disabled={userType === 10}>
                    Assigned Logbooks
                  </Accordion.Control>
                </Accordion.Item>

                <Accordion.Item
                  value="permissions"
                  className={accOpenStyles("permissions")}>
                  <Accordion.Control disabled={userType === 10}>
                    Permissions
                  </Accordion.Control>
                </Accordion.Item>

                <Accordion.Item
                  value="additional"
                  className={accOpenStyles("additional")}>
                  <Accordion.Control>Additional Info</Accordion.Control>
                </Accordion.Item>

                {userId && (
                  <Accordion.Item
                    value="protected"
                    className={accOpenStyles("protected")}>
                    <Accordion.Control>Protected Info</Accordion.Control>
                  </Accordion.Item>
                )}
              </Accordion>
              {showLoader ? (
                <Loader wrapperStyle={{ marginTop: 120 }} />
              ) : (
                <Accordion
                  defaultValue="details"
                  w="100%"
                  variant="filled"
                  value={accordionValue}
                  onChange={setAccordionValue}
                  transitionDuration={0}
                  classNames={{
                    content: styles.accordionContent,
                    item: styles.accordionItem,
                  }}>
                  <Accordion.Item
                    value="details"
                    style={{
                      border: "none",
                      background: "none",
                      margin: 0,
                    }}>
                    <Accordion.Panel>
                      <Details
                        register={register}
                        control={control}
                        setValue={setValue}
                        onTypeChange={handleTypeChange}
                        types={data?.selects.types || []}
                        ranks={selects.ranks}
                        errors={formState.errors}
                        showDateJoined={initialValues?.datejoined !== undefined}
                      />
                    </Accordion.Panel>
                  </Accordion.Item>

                  <Accordion.Item
                    value="logbooks"
                    style={{ border: "none", background: "none", margin: 0 }}>
                    <Accordion.Panel>
                      <AssignedLogbooks
                        getValues={getValues}
                        setValue={setValue}
                        control={control}
                        logbooks={data?.selects.logbooks ?? []}
                        disabled={disabled}
                        noScroll
                      />
                    </Accordion.Panel>
                  </Accordion.Item>

                  <Accordion.Item
                    value="permissions"
                    style={{ border: "none", background: "none", margin: 0 }}>
                    <Accordion.Panel>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 20,
                          alignItems: userType ? "stretch" : "center",
                        }}>
                        {!userType && (
                          <Text c="dimmed" pt={20} fs="italic">
                            First select a user type in the Details tab.
                          </Text>
                        )}
                        {selects.adminPerms.length > 0 && (
                          <CheckboxGroup
                            title="Admin"
                            data={selects.adminPerms}
                            control={control}
                            name="perms"
                            disabled={disabled}
                            renderLabel={perm => (
                              <>
                                <span style={{ flex: 1, paddingRight: 2 }}>
                                  {perm.label}
                                </span>
                                <span style={{ flex: 1.5 }}>
                                  {perm.description}
                                </span>
                              </>
                            )}
                          />
                        )}
                        {selects.userPerms.length > 0 && (
                          <CheckboxGroup
                            title="User"
                            data={selects.userPerms}
                            control={control}
                            name="perms"
                            disabled={disabled}
                            renderLabel={perm => (
                              <>
                                <span style={{ flex: 1, paddingRight: 2 }}>
                                  {perm.label}
                                </span>
                                <span style={{ flex: 1.5 }}>
                                  {perm.description}
                                </span>
                              </>
                            )}
                          />
                        )}
                      </div>
                    </Accordion.Panel>
                  </Accordion.Item>

                  <Accordion.Item
                    value="additional"
                    style={{
                      border: "none",
                      background: "none",
                      margin: 0,
                    }}>
                    <Accordion.Panel>
                      <AdditionalInfo
                        control={control}
                        register={register}
                        errors={formState.errors}
                        setValue={setValue}
                        getValues={getValues}
                      />
                    </Accordion.Panel>
                  </Accordion.Item>

                  {userId && (
                    <Accordion.Item
                      value="protected"
                      style={{
                        border: "none",
                        background: "none",
                        margin: 0,
                      }}>
                      <Accordion.Panel>
                        <ProtectedInfo userId={userId} />
                      </Accordion.Panel>
                    </Accordion.Item>
                  )}
                </Accordion>
              )}
            </LayoutContainer>
          </Flex>
        </Form>
      </PageContainer>
    </>
  );
};

export default UserForm;
